import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Field } from 'formik'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { RadioGroup, Checkbox } from '@/components/forms/formik'
import { Card } from '@/components/cards/Card'
import { fetchSentimentData } from '@/reducers/commandCenter/commandCenter.actions'
import {
  fetchSavedFilters,
  createSavedFilter,
  updateSavedFilter,
} from '@/reducers/savedFilters/savedFilters.actions'
import { SENTIMENT_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'

import { SentimentList } from './SentimentList'

export const Sentiment = () => {
  const [sentimentConfigFetched, setSentimentConfigFetched] = useState(false)
  const [isConfigView, setIsConfigView] = useState(false)
  const dispatch = useDispatch()
  const { loading, sentiment } = useSelector((state) => state.commandCenter)
  const { savedFilterList } = useSelector((state) => state.savedFilters)
  const sentimentConfig = !isEmpty(savedFilterList[SENTIMENT_FILTER_TYPE])
    ? savedFilterList[SENTIMENT_FILTER_TYPE][0]
    : {}
  const hasExistingSentimentConfig = !isEmpty(sentimentConfig) && !isEmpty(sentimentConfig.filters)
  const initialValues = {
    ignoreWeekends: hasExistingSentimentConfig ? sentimentConfig?.filters.ignoreWeekends : false,
    timePeriod: hasExistingSentimentConfig ? sentimentConfig?.filters.timePeriod : 'daily',
  }

  const handleSaveSentimentConfig = (values) => {
    const filterToSave = {
      name: 'Sentiment',
      isDefault: false,
      filterType: SENTIMENT_FILTER_TYPE,
      ...values,
    }

    if (!hasExistingSentimentConfig) {
      dispatch(createSavedFilter(filterToSave, SENTIMENT_FILTER_TYPE))
    } else {
      dispatch(updateSavedFilter(sentimentConfig.uuid, filterToSave, SENTIMENT_FILTER_TYPE))
    }

    setIsConfigView(false)
  }

  useEffect(() => {
    async function initialSetup() {
      await dispatch(fetchSavedFilters(SENTIMENT_FILTER_TYPE))

      if (!sentimentConfigFetched) {
        setSentimentConfigFetched(true)
      }
    }

    initialSetup()
  }, [])

  useEffect(() => {
    if (sentimentConfigFetched) {
      dispatch(fetchSentimentData())
    }
  }, [
    sentimentConfigFetched,
    sentimentConfig?.filters?.ignoreWeekends,
    sentimentConfig?.filters?.timePeriod,
  ])

  return (
    <Card
      title={`Sentiment Analysis ${isConfigView ? 'Configuration' : ''}`}
      buttons={
        isConfigView ? (
          <div
            className="flex flex-align-center flex-space-between small-gap"
            data-testid="config-options-container"
          >
            <Button
              onClick={() => {
                setIsConfigView(false)
              }}
              data-testid="discard-button"
            >
              Cancel
            </Button>
            <Button brand type="submit" form="sentiment-config-form" data-testid="save-button">
              Save
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => {
              setIsConfigView(true)
            }}
            data-testid="edit-button"
          >
            Edit
          </Button>
        )
      }
      pills={
        isConfigView ? null : (
          <div className="flex-align-center medium-gap">
            <Pill small emphasized data-testid="time-period-label">
              {sentimentConfig.filters?.timePeriod === 'weekly' ? 'Last 7 Days' : 'Last 24 hours'}
            </Pill>
            {sentimentConfig.filters?.ignoreWeekends && (
              <Pill small emphasized data-testid="ignore-weekends-label">
                Ignoring Weekends
              </Pill>
            )}
          </div>
        )
      }
    >
      {isConfigView ? (
        <Formik initialValues={initialValues}>
          {({ values }) => (
            <form
              id="sentiment-config-form"
              onSubmit={(event) => {
                event.preventDefault()
                handleSaveSentimentConfig(values)
              }}
              data-testid="config-form"
            >
              <section className="sentiment-config">
                <div>
                  <h3>Time Period</h3>
                  <p className="muted-text">Display calls for the last 24 hours or past 7 days</p>
                </div>
                <div>
                  <Card>
                    <Field
                      name="timePeriod"
                      component={RadioGroup}
                      options={[
                        { value: 'daily', text: 'Daily' },
                        { value: 'weekly', text: 'Weekly' },
                      ]}
                    />
                  </Card>
                  <Card>
                    <Field
                      label="Ignore Weekends"
                      name="ignoreWeekends"
                      component={Checkbox}
                      toggle
                      inline
                    />
                  </Card>
                </div>
              </section>
            </form>
          )}
        </Formik>
      ) : (
        <SentimentList loading={loading} sentiment={sentiment} />
      )}
    </Card>
  )
}
